
import React, {useContext, useEffect, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader, TextField,
    Tooltip,
    Typography
} from '@mui/material';
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import {stringToColor} from "../../Helpers/TextStyling";
import {PAContext} from "../../../Services/PAContext";
import {
    Timeline,
    TimelineConnector, TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator
} from "@mui/lab";
import {getAllContactsFromDatabase} from "../Contacts/BrowserDatabaseFunctions";
dayjs.extend(utc);
dayjs.extend(duration);

// Helper function to calculate flight duration
const calculateFlightDuration = (departTs, departOffset, arriveTs, arriveOffset) => {
    const depart = dayjs.utc(departTs).add(departOffset, 'hour');
    const arrive = dayjs.utc(arriveTs).add(arriveOffset, 'hour');
    const diff = dayjs.duration(arrive.diff(depart));
    return `${diff.hours()}h ${diff.minutes()}m`;
};

function FlightSummary({ flightMode, setFlightMode, selectedFlights, handleUpdateSelectedFlights, handleAddFlightToEvent, handleDeleteFlight }) {

    const { PA_GlobalObject} = usePA_GlobalStore();

    const { dataArray } = useContext(PAContext);
    const { SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);

    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const [ viewMode, setViewMode ] = useState(flightMode);
    useEffect(() => {
        setViewMode(flightMode);
    }, [flightMode]);
    useEffect(() => {
        setFlightMode(viewMode);
    }, [viewMode]);


    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>

            {/* Mode Switcher */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
                backgroundColor: 'rgba(251, 251, 251, 0.8)',
                borderRadius: 2,
                p: 1,
                backdropFilter: 'blur(6px)'
            }}>
                <Button
                    variant={viewMode === 'getting_there' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('getting_there')}
                >
                    Getting There
                </Button>
                <Button
                    variant={viewMode === 'getting_back' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('getting_back')}
                >
                    Getting Back
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                {selectedFlights[viewMode].length > 0 ? (

                        selectedFlights[viewMode].map(flight => {
                            // ✅ Now we can declare constants
                            const formatDate = (timestamp) => dayjs(timestamp).format("D MMM");

                            const departDayChange =
                                dayjs(flight.depart_timestamp).format("YYYY-MM-DD") !==
                                dayjs(flight.arrive_timestamp).format("YYYY-MM-DD");

                            return (
                                <Card
                                    key={flight.id}
                                    sx={{
                                        borderRadius: 4,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                        mb: 3,
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-3px)' }
                                    }}
                                >
                                    <CardHeader
                                        title={
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {flight.airline_name || 'Unknown Airline'}
                                            </Typography>
                                        }
                                        subheader={
                                            <Typography variant="body2" sx={{ color: '#616161' }}>
                                                Flight #{flight.flight_number}
                                            </Typography>
                                        }
                                    />

                                    <Divider />

                                    <CardContent>
                                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                            {/* Flight Date & Duration on the same line */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                {/* Flight Date */}
                                                <Typography variant="body2" >
                                                    {dayjs(flight.depart_timestamp).format('DD MMM YYYY')}
                                                </Typography>

                                                {/* Flight Duration, right-aligned */}
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {calculateFlightDuration(
                                                        flight.depart_timestamp,
                                                        flight.depart_timezone_offset,
                                                        flight.arrive_timestamp,
                                                        flight.arrive_timezone_offset
                                                    )}
                                                </Typography>
                                            </Box>

                                            {/* Timeline */}
                                            <Timeline sx={{ padding: 0, marginTop: 1 }}>
                                                <TimelineItem>
                                                    <TimelineOppositeContent sx={{ flex: 0.5, textAlign: "right", pr: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: "bold", color: "gray" }}>
                                                            {dayjs(flight.depart_timestamp).format('h:mm A')}
                                                        </Typography>
                                                    </TimelineOppositeContent>
                                                    <TimelineSeparator>
                                                        <TimelineDot color="info">
                                                            <i className="icon-airplane" style={{ fontSize: '1rem' }} />
                                                        </TimelineDot>
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        <Typography variant="body2" sx={{ paddingTop: '10px' }}>
                                                            {flight.depart_airport_name}
                                                        </Typography>
                                                        {departDayChange && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {formatDate(flight.depart_timestamp)}
                                                            </Typography>
                                                        )}
                                                    </TimelineContent>
                                                </TimelineItem>

                                                <TimelineItem>
                                                    <TimelineOppositeContent sx={{ flex: 0.5, textAlign: "right", pr: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: "bold", color: "gray" }}>
                                                            {dayjs(flight.arrive_timestamp).format('h:mm A')}
                                                        </Typography>
                                                    </TimelineOppositeContent>
                                                    <TimelineSeparator>
                                                        <TimelineDot color="info">
                                                            <i className="icon-airplane" style={{ fontSize: '1rem', transform: 'rotate(90deg)' }} />
                                                        </TimelineDot>
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        <Typography variant="body2" sx={{ paddingTop: '10px' }}>
                                                            {flight.arrive_airport_name}
                                                        </Typography>
                                                        {departDayChange && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {formatDate(flight.arrive_timestamp)}
                                                            </Typography>
                                                        )}
                                                    </TimelineContent>
                                                </TimelineItem>
                                            </Timeline>

                                        </Box>

                                        <Divider sx={{ mb: 2 }} />

                                        {/* Passengers */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Passengers:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                                                {PA_GlobalObject.Planning.attendees.length > 0 ? (
                                                    PA_GlobalObject.Planning.attendees
                                                        .filter(a => a.role !== 'planner')
                                                        .sort((a, b) => {
                                                            const aName = a.nickname?.trim() || a.fname || '';
                                                            const bName = b.nickname?.trim() || b.fname || '';
                                                            return aName.toLowerCase().localeCompare(bName.toLowerCase());
                                                        })
                                                        .map((attendee, index) => {
                                                            const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === attendee.id);
                                                            const fullName = attendee.nickname?.trim()
                                                                ? attendee.nickname
                                                                : `${attendee.fname} ${attendee.lname}`;

                                                            // Check if this attendee is currently on this flight
                                                            const isSelected = (flight.attendees || []).some(fa => fa.id === attendee.id);

                                                            const handleToggle = (attendee) => {
                                                                const updatedFlights = selectedFlights[viewMode].map(f => {
                                                                    if (f.id !== flight.id) return f;

                                                                    // Ensure we have an array to work with
                                                                    const currentAttendees = Array.isArray(f.attendees) ? f.attendees : [];
                                                                    const isSelected = currentAttendees.some(a => a.id === attendee.id);

                                                                    const newAttendees = isSelected
                                                                        ? currentAttendees.filter(a => a.id !== attendee.id)
                                                                        : [...currentAttendees, { id: attendee.id, cost: 0, is_business_expense: false /* ...rest */ }];

                                                                    return { ...f, attendees: newAttendees };
                                                                });

                                                                handleUpdateSelectedFlights(viewMode, updatedFlights);
                                                            };

                                                            return (
                                                                <Tooltip key={attendee.id} title={fullName} arrow>
                                                                    <Avatar
                                                                        onClick={() => handleToggle(attendee)}
                                                                        src={attendeeAvatar?.avatar?.preview || undefined}
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            zIndex: index,
                                                                            marginRight: index !== attendeesReferenceArray.length - 1 ? '-7px' : 0,
                                                                            width: '25px',
                                                                            height: '25px',
                                                                            opacity: isSelected ? 1 : 0.4,
                                                                            transition: 'opacity 0.2s ease-in-out',
                                                                            boxShadow: isSelected
                                                                                ? '0px 0px 6px rgba(25, 118, 210, 0.6)'
                                                                                : '0px 1px 2px rgba(0,0,0,0.2)',
                                                                            border: isSelected
                                                                                ? '2px solid #1976d2'
                                                                                : `1px solid ${stringToColor(fullName)}`,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: '#fff',
                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                            fontWeight: '400',
                                                                            fontSize: '14px',
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        {!attendeeAvatar?.avatar?.preview && fullName[0]}
                                                                    </Avatar>
                                                                </Tooltip>
                                                            );
                                                        })
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#9e9e9e' }}>No passengers available</Typography>
                                                )}
                                            </Box>
                                        </Box>


                                        {/* Costs */}
                                        <Box sx={{ mb: 1 }}>
                                            {/* Top line: Costs label + Total */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Costs:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    Total: {new Intl.NumberFormat(undefined, {
                                                    style: 'currency',
                                                    currency: flight.currency || 'USD',
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                }).format((flight.attendees || []).reduce((sum, a) => sum + (parseFloat(a.cost) || 0), 0))}
                                                </Typography>
                                            </Box>

                                            {/* Individual passenger costs */}
                                            {(flight.attendees || []).length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {flight.attendees.map((att) => {
                                                        const attendeeData = attendeesReferenceArray.find(a => a.id === att.id);
                                                        const fullName = attendeeData
                                                            ? attendeeData.nickname?.trim() || `${attendeeData.fname} ${attendeeData.lname}`
                                                            : att.id;

                                                        return (
                                                            <Box key={att.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="caption">{fullName}</Typography>
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    inputProps={{ min: 0, step: 0.01 }}
                                                                    value={att.cost || 0}
                                                                    onChange={(e) => {
                                                                        const value = parseFloat(e.target.value) || 0;

                                                                        const updatedFlights = selectedFlights[viewMode].map(f => {
                                                                            if (f.id !== flight.id) return f;

                                                                            const updatedAttendees = f.attendees.map(a =>
                                                                                a.id === att.id ? { ...a, cost: value } : a
                                                                            );

                                                                            return { ...f, attendees: updatedAttendees };
                                                                        });

                                                                        handleUpdateSelectedFlights(viewMode, updatedFlights);
                                                                    }}
                                                                    sx={{ width: 110, textAlign: 'right' }}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>No passengers selected</Typography>
                                            )}
                                        </Box>

                                    </CardContent>

                                    <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                                        {/* Left-aligned: Add to Event */}
                                        {PA_GlobalObject.Planning.flights.some(f =>
                                            (f._id && f._id === flight.id) ||
                                            (f.id && f.id === flight.id) ||
                                            (f.temp_id && f.temp_id === flight.temp_id)
                                        ) ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                                disabled
                                                startIcon={<i className="icon-check" />}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                Added
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleAddFlightToEvent(flight.id)}
                                            >
                                                Add to Event
                                            </Button>
                                        )}

                                        {/* Right-aligned: Trash */}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteFlight(flight.id)}
                                            title="Remove Flight Option"
                                        >
                                            <i className="icon-trash" />
                                        </IconButton>
                                    </CardActions>

                                </Card>
                            );
                        })
                ) : (
                    <Typography sx={{ textAlign: 'center', mt: 4 }} color="text.secondary">
                        No flights selected for {viewMode === 'getting_there' ? 'Getting There' : 'Getting Back'}.
                    </Typography>
                )}
            </Box>

        </div>
    );

};


export default FlightSummary;
